import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ArrowLeft, LayoutDashboard, ImageIcon, FileText, Settings as SettingsIcon, Users, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { adminLogout } from "@/lib/api";

const NAV_ITEMS = [
  { to: "/admin/dashboard", label: "Leads", icon: LayoutDashboard, testid: "admin-nav-leads" },
  { to: "/admin/media", label: "Media", icon: ImageIcon, testid: "admin-nav-media" },
  { to: "/admin/brochures", label: "Brochures", icon: FileText, testid: "admin-nav-brochures" },
  { to: "/admin/directors", label: "Directors", icon: Users, testid: "admin-nav-directors" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "admin-nav-settings" },
];

const AdminLayout = ({ children, eyebrow, title, description, actions }) => {
  const navigate = useNavigate();
  const onLogout = async () => {
    // Server clears the httpOnly cookie; nothing to remove client-side.
    await adminLogout();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header data-testid="admin-topbar" className="sticky top-0 z-40 glass-strong border-b border-white/10">
        <div className="container-narrow flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="admin-back-to-site"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/70 hover:text-[color:var(--sp-gold)] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to site
            </Link>
            <span className="hidden md:inline-block h-4 w-px bg-white/15" />
            <p className="hidden md:block text-[11px] tracking-[0.3em] uppercase text-[color:var(--sp-gold)]">
              Admin Console
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/55">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
        {/* Sub-nav */}
        <nav className="border-t border-white/8 bg-black/30">
          <div className="container-narrow flex items-center gap-1 overflow-x-auto no-scrollbar">
            {NAV_ITEMS.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  cn(
                    "inline-flex items-center gap-2 px-4 py-3 text-[11px] tracking-[0.22em] uppercase whitespace-nowrap border-b-2 transition-colors",
                    isActive
                      ? "text-[color:var(--sp-gold)] border-[color:var(--sp-gold)]"
                      : "text-white/65 border-transparent hover:text-white"
                  )
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="container-narrow py-10 overflow-x-hidden">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 min-w-0">
          <div className="min-w-0 flex-1">
            {eyebrow && (
              <p className="text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
                <span className="gold-divider" /> {eyebrow}
              </p>
            )}
            {title && <h1 className="font-display text-3xl sm:text-4xl md:text-5xl mt-3 break-words">{title}</h1>}
            {description && (
              <p className="text-white/60 text-sm mt-2 max-w-2xl">{description}</p>
            )}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
        </div>
        {children}
      </main>

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "rgba(18,18,18,0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(212,175,55,0.32)",
            color: "#fff",
          },
        }}
      />
    </div>
  );
};

export default AdminLayout;
